'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import {Section} from '../primitives';
import {MastheadSkeleton} from './MastheadSkeleton';

// Mirrors ContactBody's Card: p6 / radii xl / elevated surface / hairline
// border / sm shadow — so the skeleton cards sit exactly where the real ones do.
function Card({children}: {children: React.ReactNode}) {
  return (
    <Box
      sx={theme => ({
        flex: {md: 1},
        p: 6,
        borderRadius: `${theme.radii.xl}px`,
        backgroundColor: theme.palette.colors.surface.neutral.default,
        border: `1px solid ${theme.palette.colors.border.neutral.default}`,
        boxShadow: theme.customShadows.sm,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      })}
    >
      {children}
    </Box>
  );
}

function Field() {
  return <Skeleton variant="rounded" animation="wave" width="100%" height={51} sx={theme => ({borderRadius: `${theme.radii.full}px`})} />;
}

// Mirrors the contact page: a BannerCard masthead (160/350) over a two-column
// band — a message-form card (name row → phone → email → inquiry → message →
// submit) beside a contact-info card (title → list of contact rows).
export function ContactPageSkeleton() {
  return (
    <>
      <MastheadSkeleton mobileHeight={160} desktopHeight={350} />
      <Section>
        <Container maxWidth="lg" sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: {md: 'flex-start'}, gap: 6}}>
          <Card>
            <Skeleton variant="text" animation="wave" width={200} sx={{fontSize: theme => theme.font.size['title-4']}} />
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2.5}}>
              <Field />
              <Field />
            </Box>
            <Field />
            <Field />
            <Field />
            <Skeleton variant="rounded" animation="wave" width="100%" height={120} sx={theme => ({borderRadius: `${theme.radii.xl}px`})} />
            <Skeleton
              variant="rounded"
              animation="wave"
              width="100%"
              height={48}
              sx={theme => ({borderRadius: `${theme.radii.button}px`})}
            />
          </Card>
          <Card>
            <Skeleton variant="text" animation="wave" width={180} sx={{fontSize: theme => theme.font.size['title-4'], mb: 1}} />
            {Array.from({length: 4}, (_, i) => (
              // ContactInfoItem: a ~20px glyph, top-aligned (align-items flex-start,
              // small top offset), gap 1.5, beside a 16px label over a 14px value.
              <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                <Skeleton variant="rounded" animation="wave" width={20} height={20} sx={{mt: 0.25, flexShrink: 0}} />
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5}}>
                  <Skeleton variant="text" animation="wave" width="40%" sx={{fontSize: theme => theme.font.size['body-2']}} />
                  <Skeleton variant="text" animation="wave" width="60%" sx={{fontSize: theme => theme.font.size['body-3']}} />
                </Box>
              </Box>
            ))}
          </Card>
        </Container>
      </Section>
    </>
  );
}

ContactPageSkeleton.displayName = 'ContactPageSkeleton';
